/** The 30-second pitch: what Praxis is and why it matters, in plain language.
 *  No jargon - this is the page you send someone who asks "what is this?". */
export function WhyPraxis() {
  return (
    <>
      <div className="hero">
        <h2>Why Praxis?</h2>
        <p className="lead">Safe automation for crypto treasuries - a company card with spending limits, not the bank’s master password.</p>
      </div>

      <div className="card">
        <h3>The impossible choice teams face today</h3>
        <div className="vs">
          <div className="opt">
            <h4>🔓 Give a bot the private key</h4>
            <p className="muted">Convenient - it can pay, stake, vote automatically. But that bot (or a hacker who steals the key) can drain everything. Total trust, total risk.</p>
          </div>
          <div className="opt">
            <h4>🐢 Sign everything by hand</h4>
            <p className="muted">A hardware wallet or multisig is safe - but slow, manual, and it doesn’t scale or automate. Nobody wants to sign 30 transactions a month.</p>
          </div>
        </div>
        <p className="hint">In crypto there’s nothing in between - it’s all-or-nothing. Either you hand over the key, or you do everything yourself.</p>
      </div>

      <div className="card">
        <h3>Praxis is the missing middle</h3>
        <p>
          You set rules - <i>“this bot may spend at most 100 a day, only to these addresses, big payouts need 2 approvals, and it can never touch the rest”</i> - and Praxis enforces them. The key that
          actually holds the funds <b>never gives away full control</b>.
        </p>
        <p className="muted">Think of it like a company debit card: a limit, a list of approved vendors, and a receipt for every purchase - instead of handing someone the password to your whole bank account.</p>
      </div>

      <div className="card">
        <h3>Why it’s different - three things, together</h3>
        <div className="grid">
          <div className="feature">
            <div className="ic">🛡️</div>
            <h4>Non-custodial</h4>
            <p>Even if Praxis’s server is hacked at 3am, the attacker can only do what the on-chain permission allows - never drain the treasury. The blockchain itself enforces the ceiling. You never trust Praxis with your funds.</p>
          </div>
          <div className="feature">
            <div className="ic">⚙️</div>
            <h4>Rules like AWS IAM</h4>
            <p>Fine-grained, changeable permissions - limits, allow-lists, approvals, time windows - that you edit any time, without ever moving keys.</p>
          </div>
          <div className="feature">
            <div className="ic">🔗</div>
            <h4>Tamper-proof audit</h4>
            <p>Every decision - allowed or denied - is logged and anchored on-chain. A court-grade record of exactly what happened, that no one can rewrite.</p>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>One example that explains it all</h3>
        <div className="big-ex">
          <p>
            A DAO has <b>$5M</b> in its treasury. Every month it pays <b>28 contributors</b>, stakes, and votes. Without Praxis it must either hand a script the key to $5M (insane) - or have five people
            manually sign 30 transactions every month (painful).
          </p>
          <p style={{ marginBottom: 0 }}>
            With Praxis, a <b>bot does it automatically</b> - but only within “max X per month, only these people, anything large needs board approval.” If the bot’s server is hacked at 3am, the treasury is
            <b> safe</b>: the rules plus the blockchain are a hard ceiling the attacker can’t cross.
          </p>
        </div>
      </div>

      <div className="card">
        <h3>How it works - in 4 steps</h3>
        <ol>
          <li>
            <b>The treasury signs ONE on-chain permission</b> (a “grant”): “an operator key may act on my behalf, up to this much.” Its real key never touches Praxis. <span className="muted">(this is the non-custodial part)</span>
          </li>
          <li>
            <b>You write a policy</b> - the rules: limits, allowed recipients, approvals. Use templates or the wizard in <a href="#/policies">Policies</a>.
          </li>
          <li>
            <b>A request comes in</b> (an “intent”) → Praxis checks the policy <i>and</i> the on-chain grant → it executes the action, or denies it with a reason. See <a href="#/intents">Intents</a>.
          </li>
          <li>
            <b>Every decision is logged</b> and anchored on-chain - see <a href="#/audit">Audit</a>.
          </li>
        </ol>
        <p className="hint">
          Want the diagram and a 3-step “try it”? Go to <a href="#/guide">Start here</a>.
        </p>
      </div>
    </>
  );
}
